import {
  AuthUpdateEvent,
  AuthUpdateEventDetail,
  VersionUpdateEvent,
  VersionUpdateEventDetail,
} from './backendClient';

/**
 * This is a type helper, allowing addXListener to accept narrower callbacks than addEventListener
 * needs. This wrapper handles the technically-possible-but-highly-unlikely edge case where the
 * wrong event is dispatched to the wrong event type.
 */
export function wrappedListener<T extends Event>(
  EventClass: new (...args: any[]) => T,
  listener: (event: T) => void,
) {
  return (event: Event) => {
    if (event instanceof EventClass) {
      listener(event);
    } else {
      console.warn(
        `wrappedListener for event type ${event.type} expected event of class ${EventClass.name}, but received ${event.constructor.name}. This is almost certainly a bug in the event dispatching logic.`,
      );
    }
  };
}

/**
 * This is a thin wrapper around EventTarget, a custom event management provided by the browser.
 * This class adds concrete event labels and corresponding types, nothing more.
 */
export class ClipsApiEventTarget extends EventTarget {
  /**
   * Listeners that have been added, used only for removing listeners. Because we wrap listeners, we
   * need to keep track of the outer function corresponding to a given internal listener, which is
   * the actual thing we'll remove when asked by the caller.
   *
   * Each is a WeakMap under the given event name in the outer map. Maps maps maps. Maps.
   */
  private wrappedListeners: Map<string, WeakMap<Function, EventListener>> =
    new Map();

  private addListener<T extends Event>(
    event: string,
    EventClass: new (...args: any[]) => T,
    listener: (event: T) => void,
  ) {
    const eventListeners = this.wrappedListeners.get(event) ?? new WeakMap();
    const cachedWrapped = eventListeners.get(listener);

    const wrapped = cachedWrapped ?? wrappedListener(EventClass, listener);
    this.addEventListener(event, wrapped);

    // Start listening and add to the map.
    eventListeners.set(listener, wrapped);
    this.wrappedListeners.set(event, eventListeners);
  }

  private removeListener(
    event: string,
    listener: (event: CustomEvent) => void,
  ) {
    const eventListeners = this.wrappedListeners.get(event);

    if (eventListeners) {
      const cachedWrapped = eventListeners.get(listener);

      if (cachedWrapped) {
        // Stop listening and remove from the map.
        this.removeEventListener(event, cachedWrapped);
        eventListeners.delete(listener);
      }
    }
  }

  addAuthListener(listener: (event: AuthUpdateEvent) => void) {
    this.addListener('auth-update', AuthUpdateEvent, listener);
  }

  addVersionListener(listener: (event: VersionUpdateEvent) => void) {
    this.addListener('version-update', VersionUpdateEvent, listener);
  }

  removeAuthListener(listener: (event: AuthUpdateEvent) => void) {
    this.removeListener('auth-update', listener);
  }

  removeVersionListener(listener: (event: VersionUpdateEvent) => void) {
    this.removeListener('version-update', listener);
  }

  dispatchAuthUpdateEvent(detail: AuthUpdateEventDetail) {
    this.dispatchEvent(new AuthUpdateEvent(detail));
  }

  dispatchVersionUpdateEvent(detail: VersionUpdateEventDetail) {
    this.dispatchEvent(new VersionUpdateEvent(detail));
  }
}
